import { Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
import SignupConfirmPage from '@/pages/SignupConfirmPage';
import RequireAuth from '@/auth/RequireAuth';
import RequireTenant from '@/auth/RequireTenant';
import SelectTenantPage from '@/pages/SelectTenantPage';
import NoTenantsPage from '@/pages/NoTenantsPage';
import SignupTenantPage from '@/pages/SignupTenantPage';
import AcceptInvitationPage from '@/pages/AcceptInvitationPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import AppLayout from '@/layouts/AppLayout';
import OrganizationsPage from '@/pages/OrganizationsPage';
import ProfilePage from '@/pages/ProfilePage';
import AccountPage from '@/pages/account/AccountPage';
import ContractorsPage from '@/pages/ContractorsPage';
import ContractorCardPage from '@/pages/ContractorCardPage';
import NomenclaturePage from '@/pages/NomenclaturePage';
import NomenclatureCardPage from '@/pages/NomenclatureCardPage';
import ContractsRegistryPage from '@/pages/ContractsRegistryPage';
import NomenclatureKindsPage from '@/pages/refs/NomenclatureKindsPage';
import NomenclatureGroupsPage from '@/pages/NomenclatureGroupsPage';
import ContractCivilKindsPage from '@/pages/refs/ContractCivilKindsPage';
import CashFlowItemsPage from '@/pages/refs/CashFlowItemsPage';
import CostItemsPage from '@/pages/refs/CostItemsPage';
import ContractorGroupsPage from '@/pages/refs/ContractorGroupsPage';
import PriceTypesPage from '@/pages/refs/PriceTypesPage';
import AccountRulesPage from '@/pages/AccountRulesPage';
import ProductionAccountRulesPage from '@/pages/ProductionAccountRulesPage';
import ContractAccountRulesPage from '@/pages/ContractAccountRulesPage';
import ContractEditorPage from '@/pages/ContractEditorPage';
import PayrollAccountRulesPage from '@/pages/PayrollAccountRulesPage';
import AccrualTypesPage from '@/pages/refs/AccrualTypesPage';
import WorkSchedulesPage from '@/pages/refs/WorkSchedulesPage';
import ProductionCalendarPage from '@/pages/refs/ProductionCalendarPage';
import StaffSchedulesPage from '@/pages/payroll/StaffSchedulesPage';
import StaffSchedulePrintPage from '@/pages/payroll/StaffSchedulePrintPage';
import StaffScheduleDocPrintPage from '@/pages/payroll/StaffScheduleDocPrintPage';
import TimesheetPrintPage from '@/pages/TimesheetPrintPage';
import PlannedAccrualOrdersPage from '@/pages/payroll/PlannedAccrualOrdersPage';
import PlannedAccrualOrderEditor from '@/pages/payroll/PlannedAccrualOrderEditor';
import PlannedAccrualOrderPrintPage from '@/pages/payroll/PlannedAccrualOrderPrintPage';
import ChartOfAccountsPage from '@/pages/ChartOfAccountsPage';
import AuditLogPage from '@/pages/AuditLogPage';
import EdoJournalRoute from '@/pages/EdoJournalRoute';
import EdoCardPage, { OrganizationRoute } from '@/pages/EdoCardPage';
import SignControlPage from '@/pages/SignControlPage';
import PostingMonitorPage from '@/pages/PostingMonitorPage';
import CommerceJournalPage from '@/pages/CommerceJournalPage';
import OrganizationWizardPage from '@/pages/OrganizationWizardPage';
import EdoSignPage from '@/pages/EdoSignPage';
import DeletedDocsJournalPage from '@/pages/DeletedDocsJournalPage';
import { PropCoefEditor, PropRecalcEditor, EVCVAEditor } from '@/pages/taxation/VatProportionEditors';
import { TPInventoryEditor, TPTzrEditor } from '@/pages/taxation/TaxPurposeAdjEditors';
import TPInventoryPage from '@/pages/taxation/TPInventoryPage';
import TPTzrPage from '@/pages/taxation/TPTzrPage';
import HomePage from '@/pages/HomePage';
import ManualOperationsPage from '@/pages/ManualOperationsPage';
import ManualOperationEditor from '@/pages/ManualOperationEditor';
import ManualOperationPrintPage from '@/pages/ManualOperationPrintPage';
import GoodsReceiptsPage from '@/pages/GoodsReceiptsPage';
import GoodsReceiptEditor from '@/pages/GoodsReceiptEditor';
import GoodsSalesPage from '@/pages/GoodsSalesPage';
import SettlementsByDocumentsPage from './pages/SettlementsByDocumentsPage';
import CustomerInvoicePrintPage from './pages/CustomerInvoicePrintPage';
import GoodsSaleEditor from '@/pages/GoodsSaleEditor';
import GoodsSalePrintPage from '@/pages/GoodsSalePrintPage';
import ProductionServiceActPrintPage from '@/pages/ProductionServiceActPrintPage';
import WipInventoryPrintPage from '@/pages/WipInventoryPrintPage';
import CashOrderPrintPage from '@/pages/CashOrderPrintPage';
import GoodsTransfersPage from '@/pages/GoodsTransfersPage';
import GoodsTransferEditor from '@/pages/GoodsTransferEditor';
import InventoryCountsPage from '@/pages/InventoryCountsPage';
import InventoryCountEditor from '@/pages/InventoryCountEditor';
import OrdersPage from '@/pages/OrdersPage';
import OrderEditor from '@/pages/OrderEditor';
import SupplierOrderPrintPage from '@/pages/SupplierOrderPrintPage';
import AdvanceReportsPage from '@/pages/AdvanceReportsPage';
import AdvanceReportEditor from '@/pages/AdvanceReportEditor';
import AdvanceReportPrintPage from '@/pages/AdvanceReportPrintPage';
import DebtAdjustmentsPage from '@/pages/DebtAdjustmentsPage';
import DebtAdjustmentEditor from '@/pages/DebtAdjustmentEditor';
import DebtAdjustmentPrintPage from '@/pages/DebtAdjustmentPrintPage';
import DoubtfulDebtReservesPage from '@/pages/DoubtfulDebtReservesPage';
import DoubtfulDebtReserveEditor from '@/pages/DoubtfulDebtReserveEditor';
import GoodsRecognitionsPage from '@/pages/GoodsRecognitionsPage';
import GoodsRecognitionEditor from '@/pages/GoodsRecognitionEditor';
import GoodsWriteoffsPage from '@/pages/GoodsWriteoffsPage';
import GoodsWriteoffEditor from '@/pages/GoodsWriteoffEditor';
import CustomsDeclarationsPage from '@/pages/CustomsDeclarationsPage';
import CustomsDeclarationEditor from '@/pages/CustomsDeclarationEditor';
import CustomsDeclarationPrintPage from '@/pages/CustomsDeclarationPrintPage';
import AdditionalExpensesPage from '@/pages/AdditionalExpensesPage';
import AdditionalExpenseEditor from '@/pages/AdditionalExpenseEditor';
import AdditionalExpensePrintPage from '@/pages/AdditionalExpensePrintPage';
import ReconciliationActPage from '@/pages/ReconciliationActPage';
import ReconciliationActPrintPage from '@/pages/ReconciliationActPrintPage';
import InitialDataPage from '@/pages/InitialDataPage';
import OpeningBalanceEditor from '@/pages/OpeningBalanceEditor';
import PayrollsPage from '@/pages/PayrollsPage';
import AccrualsRegistryPage from '@/pages/AccrualsRegistryPage';
import EmployeeDebtsPage from '@/pages/payroll/EmployeeDebtsPage';
import PayrollEditor from '@/pages/PayrollEditor';
import TaxRemittancesPage from '@/pages/TaxRemittancesPage';
import FundReceiptsPage from '@/pages/FundReceiptsPage';
import FundReceiptEditor from '@/pages/FundReceiptEditor';
import TaxRemittanceEditor from '@/pages/TaxRemittanceEditor';
import SalaryDepositsPage from '@/pages/SalaryDepositsPage';
import SalaryDepositEditor from '@/pages/SalaryDepositEditor';
import PayrollPaymentsPage from '@/pages/PayrollPaymentsPage';
import PayrollPaymentEditor from '@/pages/PayrollPaymentEditor';
import PayrollPaymentPrintPage from '@/pages/PayrollPaymentPrintPage';
import PayrollPrintPage from '@/pages/PayrollPrintPage';
import TimesheetsPage from '@/pages/TimesheetsPage';
import TimesheetEditor from '@/pages/TimesheetEditor';
import DailyTimesheetEditor from '@/pages/DailyTimesheetEditor';
// Підпорція 1.5: legacy fixed-assets pages видалено. Replacement у pages/assets/.
import EmployeesPage from '@/pages/EmployeesPage';
import EmployeeEditor from '@/pages/EmployeeEditor';
import EmployeeCertificatePrintPage from '@/pages/EmployeeCertificatePrintPage';
import EmployeeWithDocumentPage from '@/pages/EmployeeWithDocumentPage';
import IndividualsPage from '@/pages/IndividualsPage';
import IndividualCardPage from '@/pages/IndividualCardPage';
import PositionsPage from '@/pages/PositionsPage';
import DepartmentsPage from '@/pages/DepartmentsPage';
import HireOrdersPage from '@/pages/HireOrdersPage';
import HrOrdersPage from '@/pages/HrOrdersPage';
import HireOrderEditor from '@/pages/HireOrderEditor';
import CivilLawContractEditor from '@/pages/CivilLawContractEditor';
import CivilLawContractsPage from '@/pages/CivilLawContractsPage';
import DpsCabinetPage from '@/pages/DpsCabinetPage';
import { AssetTaxPurposeListPage, AssetTaxPurposeEditor } from '@/pages/assets/AssetTaxPurposePage';
import TaxReportJ05Editor from '@/pages/TaxReportJ05Editor';
import TaxReportsJ05Page from '@/pages/TaxReportsJ05Page';
import DpsRequestCreatePage from '@/pages/DpsRequestCreatePage';
import DpsCabinetNewPage from '@/pages/DpsCabinetNewPage';
import StatReportsPage from '@/pages/stat/StatReportsPage';
import StatReportCreatePage from '@/pages/stat/StatReportCreatePage';
import StatReportEditor from '@/pages/stat/StatReportEditor';
import StatCabinetPage from '@/pages/stat/StatCabinetPage';
import HireNotificationEditor from '@/pages/HireNotificationEditor';
import HireNotificationPrintPage from '@/pages/HireNotificationPrintPage';
import HireOrderPrintPage from '@/pages/HireOrderPrintPage';
import StaffTransfersPage from '@/pages/StaffTransfersPage';
import StaffTransferEditor from '@/pages/StaffTransferEditor';
import StaffTransferPrintPage from '@/pages/StaffTransferPrintPage';
import DismissalOrdersPage from '@/pages/DismissalOrdersPage';
import DismissalOrderEditor from '@/pages/DismissalOrderEditor';
import AbsenceOrderEditor from '@/pages/AbsenceOrderEditor';
import DismissalOrderPrintPage from '@/pages/DismissalOrderPrintPage';
import ProfessionClassifierPage from '@/pages/ProfessionClassifierPage';
import NumberSchemesPage from '@/pages/NumberSchemesPage';
import MigrationWizardPage from '@/pages/MigrationWizardPage';
import MigrationRunsListPage from '@/pages/MigrationRunsListPage';
import MigrationCheckupPage from '@/pages/MigrationCheckupPage';
import AdminPage from '@/pages/AdminPage';
import FinancialReportsPage from '@/pages/FinancialReportsPage';
import FinancialStatementsPage from '@/pages/reports/FinancialStatementsPage';
import FinancialStatementCreatePage from '@/pages/reports/FinancialStatementCreatePage';
import FinancialStatementEditor from '@/pages/reports/FinancialStatementEditor';
import FinancialStatementPrintPage from '@/pages/reports/FinancialStatementPrintPage';
import ProfitTaxDeclarationsPage from '@/pages/taxation/ProfitTaxDeclarationsPage';
import ProfitTaxDeclarationCreatePage from '@/pages/taxation/ProfitTaxDeclarationCreatePage';
import ProfitTaxDeclarationEditor from '@/pages/taxation/ProfitTaxDeclarationEditor';
import ProfitTaxDeclarationPrintPage from '@/pages/taxation/ProfitTaxDeclarationPrintPage';
import ProjectsPage from '@/pages/ProjectsPage';
import ProjectPLReportPage from '@/pages/ProjectPLReportPage';
import ProfitabilityReportPage from '@/pages/ProfitabilityReportPage';
import CashFlowReportPage from '@/pages/CashFlowReportPage';
import FinancePage from '@/pages/FinancePage';
import IncomeJournalPage from '@/pages/IncomeJournalPage';
import IncomeJournalPrintPage from '@/pages/IncomeJournalPrintPage';
import TaxReportsPage from '@/pages/TaxReportsPage';
import FxRevaluationsPage from '@/pages/FxRevaluationsPage';
import FxRevaluationPrintPage from '@/pages/FxRevaluationPrintPage';
import FxRevaluationEditor from '@/pages/FxRevaluationEditor';
import BankReceiptsPage from '@/pages/BankReceiptsPage';
import BankPaymentsPage from '@/pages/BankPaymentsPage';
import MoneyDaySummaryPage from '@/pages/MoneyDaySummaryPage';
import BankDocEditor from '@/pages/BankDocEditor';
import CashReceiptsPage from '@/pages/CashReceiptsPage';
import CashPaymentsPage from '@/pages/CashPaymentsPage';
import CashDocEditor from '@/pages/CashDocEditor';
import TrialBalancePage from '@/pages/TrialBalancePage';
import TrialBalanceAccountPage from '@/pages/ledger/TrialBalanceAccountPage';
import LedgerPrintPage from '@/pages/ledger/LedgerPrintPage';
import AccountAnalysisPage from '@/pages/ledger/AccountAnalysisPage';
import AccountTurnoverPage from '@/pages/ledger/AccountTurnoverPage';
import GeneralLedgerPage from '@/pages/ledger/GeneralLedgerPage';
import ChessSheetPage from '@/pages/ledger/ChessSheetPage';
import ProductionWipReportPage from '@/pages/ProductionWipReportPage';
import GoodsBalancePage from '@/pages/GoodsBalancePage';
import GoodsReservedPage from '@/pages/GoodsReservedPage';
import InventoryAnalyticsPage from '@/pages/InventoryAnalyticsPage';
import ServiceActsPage from '@/pages/ServiceActsPage';
import TarePage from '@/pages/TarePage';
import TareEditor from '@/pages/TareEditor';
import ServiceActEditor from '@/pages/ServiceActEditor';
import ServiceActPrintPage from '@/pages/ServiceActPrintPage';
import TnnPage from '@/pages/TnnPage';
import TnnEditor from '@/pages/TnnEditor';
import TnnPrintPage from '@/pages/TnnPrintPage';
import AccountCardPage from '@/pages/AccountCardPage';
import SubcontoAnalysisPage from '@/pages/SubcontoAnalysisPage';
import VatSummaryPage from '@/pages/VatSummaryPage';
import VatDeclarationPrintPage from '@/pages/VatDeclarationPrintPage';
import TaxInvoicesOutgoingPage from '@/pages/TaxInvoicesOutgoingPage';
import TaxInvoiceOutgoingEditor from '@/pages/TaxInvoiceOutgoingEditor';
import ErpnAppealsPage from '@/pages/ErpnAppealsPage';
import ErpnAppealEditor from '@/pages/ErpnAppealEditor';
import TaxInvoicePrintPage from '@/pages/TaxInvoicePrintPage';
import VatDeclarationsPage from '@/pages/taxation/VatDeclarationsPage';
import VatDeclarationEditor from '@/pages/taxation/VatDeclarationEditor';
import SingleTaxDeclarationsPage from '@/pages/taxation/SingleTaxDeclarationsPage';
import SingleTaxDeclarationCreatePage from '@/pages/taxation/SingleTaxDeclarationCreatePage';
import SingleTaxDeclarationEditor from '@/pages/taxation/SingleTaxDeclarationEditor';
import SingleTaxDeclarationPrintPage from '@/pages/taxation/SingleTaxDeclarationPrintPage';
import DisabilityContributionReportsPage from '@/pages/taxation/DisabilityContributionReportsPage';
import DisabilityContributionReportCreatePage from '@/pages/taxation/DisabilityContributionReportCreatePage';
import DisabilityContributionReportEditor from '@/pages/taxation/DisabilityContributionReportEditor';
import DisabilityContributionReportPrintPage from '@/pages/taxation/DisabilityContributionReportPrintPage';
import VatDeclarationCreatePage from '@/pages/taxation/VatDeclarationCreatePage';
import TaxInvoicesIncomingPage from '@/pages/TaxInvoicesIncomingPage';
import TaxInvoiceIncomingEditor from '@/pages/TaxInvoiceIncomingEditor';
import GoodsReturnsFromCustomerPage from '@/pages/GoodsReturnsFromCustomerPage';
import GoodsReturnFromCustomerEditor from '@/pages/GoodsReturnFromCustomerEditor';
import GoodsReturnFromCustomerPrintPage from '@/pages/GoodsReturnFromCustomerPrintPage';
import GoodsReturnToSupplierPrintPage from '@/pages/GoodsReturnToSupplierPrintPage';
import GoodsTransferPrintPage from '@/pages/GoodsTransferPrintPage';
import MaterialRequisitionPrintPage from '@/pages/MaterialRequisitionPrintPage';
import ProductionReportPrintPage from '@/pages/ProductionReportPrintPage';
import ProductAssemblyPrintPage from '@/pages/ProductAssemblyPrintPage';
import ProductDisassemblyPrintPage from '@/pages/ProductDisassemblyPrintPage';
import MaterialTransferPrintPage from '@/pages/MaterialTransferPrintPage';
import ProcessingReceiptPrintPage from '@/pages/ProcessingReceiptPrintPage';
import MaterialReceiptPrintPage from '@/pages/MaterialReceiptPrintPage';
import ProcessingServicesSalesPrintPage from '@/pages/ProcessingServicesSalesPrintPage';
import GoodsRecognitionPrintPage from '@/pages/GoodsRecognitionPrintPage';
import GoodsWriteoffPrintPage from '@/pages/GoodsWriteoffPrintPage';
import InventoryCountPrintPage from '@/pages/InventoryCountPrintPage';
import GoodsReturnsToSupplierPage from '@/pages/GoodsReturnsToSupplierPage';
import GoodsReturnToSupplierEditor from '@/pages/GoodsReturnToSupplierEditor';
import BankImportPage from '@/pages/BankImportPage';
import BankApiConnectionsPage from '@/pages/BankApiConnectionsPage';
// Виробничий блок (Порція Виробництво.4a, 2026-05-22) — 9 list-pages.
import NomenclatureSpecificationsPage from '@/pages/NomenclatureSpecificationsPage';
import ProductionReportsPage from '@/pages/ProductionReportsPage';
import ProductAssembliesPage from '@/pages/ProductAssembliesPage';
import ProductDisassembliesPage from '@/pages/ProductDisassembliesPage';
import MaterialTransfersForProcessingPage from '@/pages/MaterialTransfersForProcessingPage';
import MaterialRequisitionsPage from '@/pages/MaterialRequisitionsPage';
import MaterialReceiptsForProcessingPage from '@/pages/MaterialReceiptsForProcessingPage';
import ProcessingReceiptsPage from '@/pages/ProcessingReceiptsPage';
import ProcessingServicesSalesPage from '@/pages/ProcessingServicesSalesPage';
import ProductionServiceActsPage from '@/pages/ProductionServiceActsPage';
import WipInventoryPage from '@/pages/WipInventoryPage';
// Виробничий блок editors (Порція Виробництво.4b, 2026-05-22) — 9 editor pages.
import NomenclatureSpecificationEditor from '@/pages/NomenclatureSpecificationEditor';
import ProductionReportEditor from '@/pages/ProductionReportEditor';
import ProductAssemblyEditor from '@/pages/ProductAssemblyEditor';
import ProductDisassemblyEditor from '@/pages/ProductDisassemblyEditor';
import MaterialTransferForProcessingEditor from '@/pages/MaterialTransferForProcessingEditor';
import MaterialRequisitionEditor from '@/pages/MaterialRequisitionEditor';
import MaterialReceiptForProcessingEditor from '@/pages/MaterialReceiptForProcessingEditor';
import ProcessingReceiptEditor from '@/pages/ProcessingReceiptEditor';
import ProcessingServicesSalesEditor from '@/pages/ProcessingServicesSalesEditor';
import ProductionServiceActEditor from '@/pages/ProductionServiceActEditor';
import WipInventoryEditor from '@/pages/WipInventoryEditor';
// Роздрібний блок list-pages (Порція Роздріб.5a, 2026-05-23) — 15 list pages.
import PriceGroupsPage from '@/pages/retail/PriceGroupsPage';
import NomenclatureRetailPricesPage from '@/pages/retail/NomenclatureRetailPricesPage';
import PosWorkstationsPage from '@/pages/retail/PosWorkstationsPage';
import TradeEquipmentPage from '@/pages/retail/TradeEquipmentPage';
import TradeEquipmentEditor from '@/pages/retail/TradeEquipmentEditor';
import CurrencyOperationsPage from '@/pages/banking/CurrencyOperationsPage';
import CurrencyOperationEditor from '@/pages/banking/CurrencyOperationEditor';
import ReconciliationActsPage from '@/pages/settlements/ReconciliationActsPage';
import ReconciliationActEditor from '@/pages/settlements/ReconciliationActEditor';
import PowersOfAttorneyPage from '@/pages/settlements/PowersOfAttorneyPage';
import PowerOfAttorneyEditor from '@/pages/settlements/PowerOfAttorneyEditor';
import SettlementInventoriesPage from '@/pages/settlements/SettlementInventoriesPage';
import SettlementInventoryEditor from '@/pages/settlements/SettlementInventoryEditor';
import PosProvidersPage from '@/pages/retail/PosProvidersPage';
import CustomerCardsPage from '@/pages/retail/CustomerCardsPage';
import LoyaltySettingsPage from '@/pages/retail/LoyaltySettingsPage';
import SalesChannelsPage from '@/pages/retail/SalesChannelsPage';
import CustomerCardIssuesPage from '@/pages/retail/CustomerCardIssuesPage';
import CashShiftOpensPage from '@/pages/retail/CashShiftOpensPage';
import RetailSalesPage from '@/pages/retail/RetailSalesPage';
import LoyaltyTransactionsPage from '@/pages/retail/LoyaltyTransactionsPage';
import RetailReturnsPage from '@/pages/retail/RetailReturnsPage';
import CashShiftClosesPage from '@/pages/retail/CashShiftClosesPage';
import PriceChangesPage from '@/pages/retail/PriceChangesPage';
import OnlineStoreOrdersPage from '@/pages/retail/OnlineStoreOrdersPage';
// Роздрібний блок editor-pages (Порція Роздріб.5b, 2026-05-23) — 15 editors.
import PriceGroupEditor from '@/pages/retail/PriceGroupEditor';
import NomenclatureRetailPriceEditor from '@/pages/retail/NomenclatureRetailPriceEditor';
import PosWorkstationEditor from '@/pages/retail/PosWorkstationEditor';
import PosProviderEditor from '@/pages/retail/PosProviderEditor';
import CustomerCardEditor from '@/pages/retail/CustomerCardEditor';
import LoyaltySettingsEditor from '@/pages/retail/LoyaltySettingsEditor';
import SalesChannelEditor from '@/pages/retail/SalesChannelEditor';
import CustomerCardIssueEditor from '@/pages/retail/CustomerCardIssueEditor';
import CashShiftOpenEditor from '@/pages/retail/CashShiftOpenEditor';
import RetailSaleEditor from '@/pages/retail/RetailSaleEditor';
import LoyaltyTransactionEditor from '@/pages/retail/LoyaltyTransactionEditor';
import RetailReturnEditor from '@/pages/retail/RetailReturnEditor';
import CashShiftCloseEditor from '@/pages/retail/CashShiftCloseEditor';
import PriceChangeEditor from '@/pages/retail/PriceChangeEditor';
import OnlineStoreOrderEditor from '@/pages/retail/OnlineStoreOrderEditor';
import POSSetupWizard from '@/pages/retail/POSSetupWizard';
// Блок 9.2 (Warehouse Orders Workflow, 2026-05-24).
import WarehouseOrdersPage from '@/pages/warehouse/WarehouseOrdersPage';
import WarehouseOrderEditor from '@/pages/warehouse/WarehouseOrderEditor';
// Підпорція 3.1a (Блок 3 ПДВ): UKTZED + tax refs (2026-05-24).
import TaxPurposeRulesPage from '@/pages/taxation/TaxPurposeRulesPage';
// Підпорція 3.1b (Блок 3 ПДВ-коригування, 2026-05-24).
import EVCVAPage from '@/pages/taxation/EVCVAPage';
import VatCreditReconciliationPage from '@/pages/taxation/VatCreditReconciliationPage';
import VatDeclarationDocPrintPage from '@/pages/taxation/VatDeclarationDocPrintPage';
// Підпорція 3.1c (Блок 3 ПДВ-документи, 2026-05-24).
import {
  AdvancePaymentsPage,
  Appendix1Page,
  PropRecalcPage,
  PropCoefPage,
} from '@/pages/taxation/VatDocumentsPages';
import AdvancePaymentEditor from '@/pages/taxation/AdvancePaymentEditor';
// Підпорція 3.2a (Блок 3 податок на прибуток, 2026-05-25).
// ч.1384: документи податку на прибуток відкривались з переліку на неіснуючу адресу.
// Підпорція 3.2b (customs + tax_declaration_articles, 2026-05-25).
import { TaxDeclarationArticlesPage } from '@/pages/taxation/TaxDeclarationArticlesPage';
import { CustomsNumbersRegisterPage } from '@/pages/taxation/CustomsNumbersRegisterPage';
// Підпорція 1.1 (Block 1: assets foundation, 2026-05-25).
import {
  AssetCommissioningPage,
  AssetWriteOffPage,
} from '@/pages/assets/AssetsPages';
// Необоротні активи v2 (2026-08-27): єдиний перелік + картка-сторінка + звіти.
import AssetsRegistryPage from '@/pages/assets/AssetsRegistryPage';
import AssetCardPage from '@/pages/assets/AssetCardPage';
import AssetsInfoPage from '@/pages/assets/AssetsInfoPage';
import DepreciationJournalPage from '@/pages/assets/DepreciationJournalPage';
import LowValueBalancesPage from '@/pages/assets/LowValueBalancesPage';
import AssetAccountSettingsPage from '@/pages/assets/AssetAccountSettingsPage';
import FixedAssetInventoryPrintPage from '@/pages/assets/FixedAssetInventoryPrintPage';
import { AssetCommissioningEditor, AssetWriteOffEditor } from '@/pages/assets/AssetDocEditors';
import { AssetCommissioningPrintPage, AssetWriteOffPrintPage, InternalTransferPrintPage, ModernizationPrintPage, StateChangePrintPage } from '@/pages/assets/AssetDocPrintPages';
import { InternalTransferEditor, InventoryEditor, StateChangeEditor } from '@/pages/assets/AssetDocEditors2';
import StateChangePage from '@/pages/assets/StateChangePage';
// Підпорція 1.2 (depreciation).
// Підпорція 1.3 (modernization + internal transfer).
import { InternalTransferPage } from '@/pages/assets/MovementPages';
// Підпорція 1.4 (inventory + sale).
import {
  InventoryPage,
} from '@/pages/assets/InventorySalePages';
// Block 7 (commission).
import {
  CommissionContractsPage,
  CommissionTransferPage,
  CommissionSalePage,
  CommissionSettlementPage,
} from '@/pages/commission/CommissionPages';
// Block 2.0+2.1 (production calendar + vacation).
import {
  VacationOrdersPage,
  VacationCalculationsPage,
} from '@/pages/vacation/VacationPages';
import VacationOrderEditor from '@/pages/vacation/VacationOrderEditor';
import VacationOrderPrintPage from '@/pages/vacation/VacationOrderPrintPage';
import VacationCalculationCreatePage from '@/pages/vacation/VacationCalculationCreatePage';
import VacationCalculationEditor from '@/pages/vacation/VacationCalculationEditor';
import VacationCompensationsPage from '@/pages/vacation/VacationCompensationsPage';
import VacationCompensationEditor from '@/pages/vacation/VacationCompensationEditor';
import VacationCompensationCreatePage from '@/pages/vacation/VacationCompensationCreatePage';
// Block 2.2 (sick leave).
import {
  SickLeavesPage,
  SickLeaveCalculationsPage,
} from '@/pages/sick_leave/SickLeavePages';
import SickLeaveEditor from '@/pages/sick_leave/SickLeaveEditor';
// Block 5.1 (month-close runner).
import { MonthCloseRunsPage } from '@/pages/month_close/MonthCloseRunsPage';
import { MonthCloseRunPage } from '@/pages/month_close/MonthCloseRunPage';
// Block 6 (accounting constants).
import { AccountingConstantsPage } from '@/pages/refs/AccountingConstantsPage';
// Block 8.2 / 8.3 (ФОП).
import {
  SingleTaxCalculationsPage,
  FopSelfEsvPaymentsPage,
  FopSelfWzPaymentsPage,
} from '@/pages/fop/FopPages';
import { FopReportsPage } from '@/pages/fop/FopReportsPage';
import FopZsoQuarterlyCalculationsPage from '@/pages/fop/FopZsoQuarterlyCalculationsPage';
// POS UI (Порція Роздріб.6, 2026-05-23) — окремий kiosk-mode без AppLayout.
import POSScreen from '@/pages/pos/POSScreen';
import POSGuard from '@/pages/pos/POSGuard';
import POSReceiptPrint from '@/pages/pos/POSReceiptPrint';
// Platform Admin Блок A (super-admin, 2FA) — окрема гілка роутингу без AppLayout.
import PlatformLayout from '@/layouts/PlatformLayout';
import PlatformSystemDataPage from '@/pages/platform/PlatformSystemDataPage';
import PlatformAccessPage from '@/pages/platform/PlatformAccessPage';
import PlatformTestersPage from '@/pages/platform/PlatformTestersPage';
import PlatformVideoPage from '@/pages/platform/PlatformVideoPage';
import PlatformVideoGuidePage from '@/pages/platform/PlatformVideoGuidePage';
import PublicHelp from '@/pages/help/PublicHelp';
import FeedbackThreadPage from '@/pages/FeedbackThreadPage';
import SupportPage from '@/pages/support/SupportPage';
import SupportConsolePage from '@/pages/support/SupportConsolePage';
import TwoFactorRecoveryPage from '@/pages/TwoFactorRecoveryPage';
import IncomingDocumentsPage from '@/pages/IncomingDocumentsPage';
import ContractorDuplicatesPage from '@/pages/ContractorDuplicatesPage';
import IncomingDocumentReviewPage from '@/pages/IncomingDocumentReviewPage';
import PostingAuditPage from '@/pages/PostingAuditPage';
import SupportAnalyticsPage from '@/pages/support/SupportAnalyticsPage';
import SupportHelpPage from '@/pages/support/SupportHelpPage';
import SupportHelpArticlePage from '@/pages/support/SupportHelpArticlePage';


export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* ПУБЛІЧНА сторінка підписання ЕДО (без логіна, за токеном). */}
      <Route path="/edo/sign/:token" element={<EdoSignPage />} />
      {/* ч.972: довідковий центр ПУБЛІЧНИЙ — клієнт шукає відповідь до
          звернення у підтримку, а посилання на статтю можна переслати
          будь-кому.
          ч.1000: маршрут ОДИН на всіх. Спроба продублювати його всередині
          «/» (щоб залогінений отримав макет) не спрацювала: React Router
          обирає точніший збіг, і абсолютний «/help» завжди вигравав у
          вкладеного — залогінений лишався без шапки й меню. Тому оболонку
          обирає сам PublicHelp: залогіненому він показує AppLayout, гостю —
          просту шапку з кнопкою «увійти». */}
      <Route path="/feedback/:token" element={<FeedbackThreadPage />} />
      <Route path="/help" element={<PublicHelp />} />
      <Route path="/help/:slug" element={<PublicHelp />} />
      <Route path="/signup" element={<SignupPage />} />
      {/* ч.1122: підтвердження пошти з листа — тут створюється обліковий запис. */}
      <Route path="/signup/confirm" element={<SignupConfirmPage />} />
      {/* Публічна accept-сторінка — без RequireAuth, бо до accept'а user
          ще не залогінений (новий user) або залогінений іншим email. */}
      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
      {/* ч.1073: відновлення пароля — публічні (користувач не може увійти). */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* ч.1091: відновлення двофакторного входу через підтримку (лист із КЕП) —
          публічна: людина без коду увійти не може. */}
      <Route path="/account-recovery/2fa" element={<TwoFactorRecoveryPage />} />
      {/* Tenant flow (Порція 3.0.5.1b): автентифікація потрібна, але
          tenant ще не виставлений. */}
      <Route
        path="/select-tenant"
        element={
          <RequireAuth>
            <SelectTenantPage />
          </RequireAuth>
        }
      />
      <Route
        path="/no-tenants"
        element={
          <RequireAuth>
            <NoTenantsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/signup-tenant"
        element={
          <RequireAuth>
            <SignupTenantPage />
          </RequireAuth>
        }
      />
      <Route
        path="/"
        element={
          <RequireAuth>
            <RequireTenant>
              <AppLayout />
            </RequireTenant>
          </RequireAuth>
        }
      >
        <Route index element={<HomePage />} />
        {/* ч.1009 (Етап 3): звернення до підтримки — лише для своїх. */}
        <Route path="support" element={<SupportPage />} />
        <Route path="organizations" element={<OrganizationsPage />} />
        {/* Картки орг/контрагентів — ПОВНОЦІННІ сторінки (2026-08-14). */}
        <Route path="organizations/new" element={<OrganizationWizardPage />} />
        <Route path="organizations/:id" element={<OrganizationRoute />} />
        <Route path="contractors" element={<ContractorsPage />} />
        {/* ч.1094: дублі контрагентів (обʼєднання після імпорту кількох баз 1С). */}
        <Route path="contractors/duplicates" element={<ContractorDuplicatesPage />} />
        <Route path="contractors/:id" element={<ContractorCardPage />} />
        <Route path="contracts-registry" element={<ContractsRegistryPage />} />
        <Route path="nomenclature" element={<NomenclaturePage />} />
        {/* ч.1059: картка номенклатури — окрема сторінка зі своєю адресою. */}
        <Route path="nomenclature/new" element={<NomenclatureCardPage />} />
        <Route path="nomenclature/:id" element={<NomenclatureCardPage />} />
        <Route path="refs/nomenclature-kinds" element={<NomenclatureKindsPage />} />
        <Route path="refs/nomenclature-groups" element={<NomenclatureGroupsPage />} />
        <Route path="refs/contract-civil-kinds" element={<ContractCivilKindsPage />} />
        <Route path="refs/cash-flow-items" element={<CashFlowItemsPage />} />
        <Route path="refs/cost-items" element={<CostItemsPage />} />
        <Route path="refs/contractor-groups" element={<ContractorGroupsPage />} />
        <Route path="refs/price-types" element={<PriceTypesPage />} />
        <Route path="refs/account-rules" element={<AccountRulesPage />} />
        <Route path="production/account-rules" element={<ProductionAccountRulesPage />} />
        <Route path="refs/contract-account-rules" element={<ContractAccountRulesPage />} />
        <Route path="refs/contracts/new" element={<ContractEditorPage />} />
        <Route path="refs/contracts/:id" element={<ContractEditorPage />} />
        <Route path="refs/payroll-account-rules" element={<PayrollAccountRulesPage />} />
        <Route path="refs/accrual-types" element={<AccrualTypesPage />} />
        <Route path="refs/work-schedules" element={<WorkSchedulesPage />} />
        <Route path="staff-schedule" element={<StaffSchedulesPage />} />
        <Route path="staff-schedule/print" element={<StaffSchedulePrintPage />} />
        <Route path="staff-schedule/:id/print" element={<StaffScheduleDocPrintPage />} />
        <Route path="timesheets/:id/print" element={<TimesheetPrintPage />} />
        <Route path="payroll/planned-accrual-orders" element={<PlannedAccrualOrdersPage />} />
        <Route path="payroll/planned-accrual-orders/new" element={<PlannedAccrualOrderEditor />} />
        <Route path="payroll/planned-accrual-orders/:id/print" element={<PlannedAccrualOrderPrintPage />} />
        <Route path="payroll/planned-accrual-orders/:id" element={<PlannedAccrualOrderEditor />} />
        <Route path="chart-of-accounts" element={<ChartOfAccountsPage />} />
        <Route path="audit-log" element={<AuditLogPage />} />
        <Route path="edo/journal" element={<EdoJournalRoute />} />
        {/* ч.1401: режим «лише ЕДО» — журнал з фіксованим напрямком; без організації — картка учасника. */}
        <Route path="edo/incoming" element={<Navigate to="/edo/journal?direction=IN" replace />} />
        <Route path="edo/outgoing" element={<Navigate to="/edo/journal" replace />} />
        <Route path="edo/cards/new" element={<EdoCardPage />} />
        <Route path="edo/cards/:id" element={<EdoCardPage />} />
        <Route path="edo/sign-control" element={<SignControlPage />} />
        <Route path="monitor/posting" element={<PostingMonitorPage />} />
        <Route path="deleted-documents" element={<DeletedDocsJournalPage />} />
        <Route path="manual-operations" element={<ManualOperationsPage />} />
        <Route path="manual-operations/:id" element={<ManualOperationEditor />} />
        <Route path="manual-operations/:id/print" element={<ManualOperationPrintPage />} />
        <Route path="goods-receipts" element={<GoodsReceiptsPage />} />
        <Route path="goods-receipts/:id" element={<GoodsReceiptEditor />} />
        {/* ч.1093: вхідні документи постачальника — розпізнавання (Oblik+/AI). */}
        <Route path="incoming-documents" element={<IncomingDocumentsPage />} />
        <Route path="incoming-documents/:id" element={<IncomingDocumentReviewPage />} />
        {/* ч.1098: аудит проводок (правила + пояснення Oblik+/AI). */}
        <Route path="audit/postings" element={<PostingAuditPage />} />
        <Route path="goods-sales" element={<GoodsSalesPage />} />
        <Route path="reports/settlements-by-documents" element={<SettlementsByDocumentsPage />} />
        {/* ч.1216: зведені журнали «Комерції» — як «Документи покупців / постачальників» у 1С. */}
        <Route path="sales-journal" element={<CommerceJournalPage side="sales" />} />
        <Route path="purchases-journal" element={<CommerceJournalPage side="purchases" />} />
        <Route path="goods-sales/:id" element={<GoodsSaleEditor />} />
        <Route path="goods-sales/:id/print" element={<GoodsSalePrintPage />} />
        <Route path="service-acts" element={<ServiceActsPage />} />
        <Route path="service-acts/:id" element={<ServiceActEditor />} />
        <Route path="service-acts/:id/print" element={<ServiceActPrintPage />} />
        <Route path="tnn" element={<TnnPage />} />
        <Route path="tnn/:id" element={<TnnEditor />} />
        <Route path="tnn/:id/print" element={<TnnPrintPage />} />
        <Route path="goods-transfers" element={<GoodsTransfersPage />} />
        <Route path="goods-transfers/:id" element={<GoodsTransferEditor />} />
        <Route path="tare-transfers" element={<TarePage key="transfer" kind="transfer" />} />
        <Route path="tare-transfers/:id" element={<TareEditor key="transfer" kind="transfer" />} />
        <Route path="tare-returns" element={<TarePage key="return" kind="return" />} />
        <Route path="tare-returns/:id" element={<TareEditor key="return" kind="return" />} />
        <Route path="inventory-counts" element={<InventoryCountsPage />} />
        <Route path="inventory-counts/:id" element={<InventoryCountEditor />} />
        <Route path="customer-orders" element={<OrdersPage key="customer-ORDER" kind="customer" forcedOrderKind="ORDER" title="Замовлення покупців" newButtonLabel="Нове замовлення" />} />
        <Route path="customer-orders/:id" element={<OrderEditor kind="customer" />} />
        <Route path="customer-invoices" element={<OrdersPage key="customer-PI" kind="customer" forcedOrderKind="PROFORMA_INVOICE" title="Рахунки покупцям" newButtonLabel="Новий рахунок" />} />
        <Route path="customer-invoices/:id" element={<OrderEditor kind="customer" />} />
        <Route path="customer-invoices/:id/print" element={<CustomerInvoicePrintPage />} />
        <Route path="supplier-orders" element={<OrdersPage key="supplier-ORDER" kind="supplier" forcedOrderKind="ORDER" title="Замовлення постачальникам" newButtonLabel="Нове замовлення" />} />
        <Route path="supplier-orders/:id" element={<OrderEditor kind="supplier" />} />
        <Route path="supplier-orders/:id/print" element={<SupplierOrderPrintPage />} />
        <Route path="supplier-invoices" element={<OrdersPage key="supplier-PI" kind="supplier" forcedOrderKind="PROFORMA_INVOICE" title="Рахунки від постачальників" newButtonLabel="Новий рахунок" />} />
        <Route path="supplier-invoices/:id" element={<OrderEditor kind="supplier" />} />
        <Route path="advance-reports" element={<AdvanceReportsPage />} />
        <Route path="advance-reports/:id" element={<AdvanceReportEditor />} />
        <Route path="advance-reports/:id/print" element={<AdvanceReportPrintPage />} />
        <Route path="debt-adjustments" element={<DebtAdjustmentsPage />} />
        <Route path="debt-adjustments/:id" element={<DebtAdjustmentEditor />} />
        <Route path="debt-adjustments/:id/print" element={<DebtAdjustmentPrintPage />} />
        <Route path="doubtful-debt-reserves" element={<DoubtfulDebtReservesPage />} />
        <Route path="doubtful-debt-reserves/:id" element={<DoubtfulDebtReserveEditor />} />
        <Route path="goods-recognitions" element={<GoodsRecognitionsPage />} />
        <Route path="goods-recognitions/:id" element={<GoodsRecognitionEditor />} />
        <Route path="goods-writeoffs" element={<GoodsWriteoffsPage />} />
        <Route path="goods-writeoffs/:id" element={<GoodsWriteoffEditor />} />
        <Route path="customs-declarations" element={<CustomsDeclarationsPage />} />
        <Route path="customs-declarations/:id" element={<CustomsDeclarationEditor />} />
        <Route path="customs-declarations/:id/print" element={<CustomsDeclarationPrintPage />} />
        <Route path="additional-expenses" element={<AdditionalExpensesPage />} />
        <Route path="additional-expenses/:id" element={<AdditionalExpenseEditor />} />
        <Route path="additional-expenses/:id/print" element={<AdditionalExpensePrintPage />} />
        <Route path="fx-revaluations" element={<FxRevaluationsPage />} />
        <Route path="fx-revaluations/:id" element={<FxRevaluationEditor />} />
        <Route path="fx-revaluations/:id/print" element={<FxRevaluationPrintPage />} />
        <Route path="bank-receipts" element={<BankReceiptsPage />} />
        <Route path="bank-receipts/:id" element={<BankDocEditor kind="receipt" />} />
        <Route path="bank-payments" element={<BankPaymentsPage />} />
        <Route path="bank-summary" element={<MoneyDaySummaryPage kind="bank" />} />
        <Route path="bank-payments/:id" element={<BankDocEditor kind="payment" />} />
        <Route path="cash-receipts" element={<CashReceiptsPage />} />
        <Route path="cash-receipts/:id" element={<CashDocEditor kind="receipt" />} />
        <Route path="cash-receipts/:id/print" element={<CashOrderPrintPage key="receipt" kind="receipt" />} />
        <Route path="cash-payments" element={<CashPaymentsPage />} />
        <Route path="cash-summary" element={<MoneyDaySummaryPage kind="cash" />} />
        <Route path="cash-payments/:id" element={<CashDocEditor kind="payment" />} />
        <Route path="cash-payments/:id/print" element={<CashOrderPrintPage key="payment" kind="payment" />} />
        <Route path="taxation/vat-declarations" element={<VatDeclarationsPage />} />
        <Route path="taxation/vat-declarations/new" element={<VatDeclarationCreatePage />} />
        <Route path="taxation/vat-declarations/:id" element={<VatDeclarationEditor />} />
        <Route path="taxation/vat-declarations/:id/print" element={<VatDeclarationDocPrintPage />} />
        {/* ч.1113: декларації платника єдиного податку */}
        <Route path="taxation/single-tax-declarations" element={<SingleTaxDeclarationsPage />} />
        <Route path="taxation/single-tax-declarations/new" element={<SingleTaxDeclarationCreatePage />} />
        <Route path="taxation/profit-tax-declarations" element={<ProfitTaxDeclarationsPage />} />
        <Route path="taxation/profit-tax-declarations/new" element={<ProfitTaxDeclarationCreatePage />} />
        <Route path="taxation/profit-tax-declarations/:id" element={<ProfitTaxDeclarationEditor />} />
        <Route path="taxation/profit-tax-declarations/:id/print" element={<ProfitTaxDeclarationPrintPage />} />
        <Route path="taxation/single-tax-declarations/:id" element={<SingleTaxDeclarationEditor />} />
        <Route path="taxation/single-tax-declarations/:id/print" element={<SingleTaxDeclarationPrintPage />} />
        {/* ч.1208: звіт про внесок на підтримку працевлаштування осіб з інвалідністю (J0500701/F0500701). */}
        <Route path="taxation/disability-contribution-reports" element={<DisabilityContributionReportsPage />} />
        <Route path="taxation/disability-contribution-reports/new" element={<DisabilityContributionReportCreatePage />} />
        <Route path="taxation/disability-contribution-reports/:id" element={<DisabilityContributionReportEditor />} />
        <Route path="taxation/disability-contribution-reports/:id/print" element={<DisabilityContributionReportPrintPage />} />
        {/* ч.1357: статистична звітність до Держстату (1-опт, 10-зез) і Кабінет респондента. */}
        <Route path="stat-cabinet" element={<StatCabinetPage />} />
        <Route path="stat-reports/1-opt" element={<StatReportsPage form="1-opt" />} />
        <Route path="stat-reports/1-opt/new" element={<StatReportCreatePage form="1-opt" />} />
        <Route path="stat-reports/1-opt/:id" element={<StatReportEditor form="1-opt" />} />
        <Route path="stat-reports/10-zez" element={<StatReportsPage form="10-zez" />} />
        <Route path="stat-reports/10-zez/new" element={<StatReportCreatePage form="10-zez" />} />
        <Route path="stat-reports/10-zez/:id" element={<StatReportEditor form="10-zez" />} />
        <Route path="tax-invoices-outgoing" element={<TaxInvoicesOutgoingPage />} />
        <Route path="tax-invoices-outgoing/new" element={<TaxInvoiceOutgoingEditor />} />
        <Route path="tax-invoices-outgoing/:id" element={<TaxInvoiceOutgoingEditor />} />
        <Route path="tax-invoices-outgoing/:id/print" element={<TaxInvoicePrintPage kind="outgoing" />} />
        {/* ч.1296: оскарження зупиненої реєстрації ПН/РК (пояснення / скарга з копіями документів). */}
        <Route path="erpn-appeals" element={<ErpnAppealsPage />} />
        <Route path="erpn-appeals/new" element={<ErpnAppealEditor />} />
        <Route path="erpn-appeals/:id" element={<ErpnAppealEditor />} />
        <Route path="tax-invoices-incoming" element={<TaxInvoicesIncomingPage />} />
        <Route path="tax-invoices-incoming/new" element={<TaxInvoiceIncomingEditor />} />
        <Route path="tax-invoices-incoming/:id" element={<TaxInvoiceIncomingEditor />} />
        <Route path="tax-invoices-incoming/:id/print" element={<TaxInvoicePrintPage kind="incoming" />} />
        <Route path="goods-returns-from-customer" element={<GoodsReturnsFromCustomerPage />} />
        <Route path="goods-returns-from-customer/:id" element={<GoodsReturnFromCustomerEditor />} />
        <Route path="goods-returns-from-customer/:id/print" element={<GoodsReturnFromCustomerPrintPage />} />
        <Route path="goods-returns-to-supplier/:id/print" element={<GoodsReturnToSupplierPrintPage />} />
        <Route path="goods-transfers/:id/print" element={<GoodsTransferPrintPage />} />
        <Route path="goods-recognitions/:id/print" element={<GoodsRecognitionPrintPage />} />
        <Route path="goods-writeoffs/:id/print" element={<GoodsWriteoffPrintPage />} />
        <Route path="inventory-counts/:id/print" element={<InventoryCountPrintPage />} />
        <Route path="goods-returns-to-supplier" element={<GoodsReturnsToSupplierPage />} />
        <Route path="goods-returns-to-supplier/new" element={<GoodsReturnToSupplierEditor />} />
        <Route path="goods-returns-to-supplier/:id" element={<GoodsReturnToSupplierEditor />} />
        {/* Виробничий блок list-pages (Порція 4a) + editors (Порція 4b). */}
        <Route path="nomenclature-specifications" element={<NomenclatureSpecificationsPage />} />
        <Route path="nomenclature-specifications/:id" element={<NomenclatureSpecificationEditor />} />
        <Route path="production-reports" element={<ProductionReportsPage />} />
        <Route path="production-reports/:id" element={<ProductionReportEditor />} />
        <Route path="production-reports/:id/print" element={<ProductionReportPrintPage />} />
        <Route path="product-assemblies" element={<ProductAssembliesPage />} />
        <Route path="product-assemblies/:id" element={<ProductAssemblyEditor />} />
        <Route path="product-assemblies/:id/print" element={<ProductAssemblyPrintPage />} />
        <Route path="product-disassemblies" element={<ProductDisassembliesPage />} />
        <Route path="product-disassemblies/:id" element={<ProductDisassemblyEditor />} />
        <Route path="product-disassemblies/:id/print" element={<ProductDisassemblyPrintPage />} />
        <Route path="material-transfers-for-processing" element={<MaterialTransfersForProcessingPage />} />
        <Route path="material-transfers-for-processing/:id" element={<MaterialTransferForProcessingEditor />} />
        <Route path="material-transfers-for-processing/:id/print" element={<MaterialTransferPrintPage />} />
        <Route path="material-requisitions" element={<MaterialRequisitionsPage />} />
        <Route path="material-requisitions/:id" element={<MaterialRequisitionEditor />} />
        <Route path="material-requisitions/:id/print" element={<MaterialRequisitionPrintPage />} />
        <Route path="material-receipts-for-processing" element={<MaterialReceiptsForProcessingPage />} />
        <Route path="material-receipts-for-processing/:id" element={<MaterialReceiptForProcessingEditor />} />
        <Route path="material-receipts-for-processing/:id/print" element={<MaterialReceiptPrintPage />} />
        <Route path="processing-receipts" element={<ProcessingReceiptsPage />} />
        <Route path="processing-receipts/:id" element={<ProcessingReceiptEditor />} />
        <Route path="processing-receipts/:id/print" element={<ProcessingReceiptPrintPage />} />
        <Route path="processing-services-sales" element={<ProcessingServicesSalesPage />} />
        <Route path="processing-services-sales/:id" element={<ProcessingServicesSalesEditor />} />
        <Route path="processing-services-sales/:id/print" element={<ProcessingServicesSalesPrintPage />} />
        <Route path="production-service-acts" element={<ProductionServiceActsPage />} />
        <Route path="production-service-acts/:id" element={<ProductionServiceActEditor />} />
        <Route path="production-service-acts/:id/print" element={<ProductionServiceActPrintPage />} />
        <Route path="wip-inventory" element={<WipInventoryPage />} />
        <Route path="wip-inventory/:id" element={<WipInventoryEditor />} />
        <Route path="wip-inventory/:id/print" element={<WipInventoryPrintPage />} />
        <Route path="reports/trial-balance" element={<TrialBalancePage />} />
        <Route path="reports/trial-balance/account" element={<TrialBalanceAccountPage />} />
        <Route path="reports/ledger-print" element={<LedgerPrintPage />} />
        <Route path="reports/account-analysis" element={<AccountAnalysisPage />} />
        <Route path="reports/account-turnover" element={<AccountTurnoverPage />} />
        <Route path="reports/general-ledger" element={<GeneralLedgerPage />} />
        <Route path="reports/chess-sheet" element={<ChessSheetPage />} />
        <Route path="reports/production-wip" element={<ProductionWipReportPage />} />
        <Route path="reports/goods-balance" element={<GoodsBalancePage />} />
        <Route path="reports/goods-reserved" element={<GoodsReservedPage />} />
        <Route path="reports/inventory-analytics" element={<InventoryAnalyticsPage />} />
        <Route path="reports/account-card" element={<AccountCardPage />} />
        <Route path="reports/subconto-analysis" element={<SubcontoAnalysisPage />} />
        <Route path="reports/reconciliation-act" element={<ReconciliationActPage />} />
        <Route path="reports/reconciliation-act/print" element={<ReconciliationActPrintPage />} />
        {/* ч.1084: «Початкові дані (без 1С)» — довідники з файлу + вступні залишки. */}
        <Route path="opening-balances" element={<InitialDataPage />} />
        <Route path="opening-balances/:id" element={<OpeningBalanceEditor />} />
        <Route path="import-csv" element={<Navigate to="/opening-balances" replace />} />
        <Route path="payrolls" element={<AccrualsRegistryPage />} />
        <Route path="payrolls/legacy" element={<PayrollsPage />} />
        <Route path="payroll-debts" element={<EmployeeDebtsPage />} />
        {/* legacy-аліас (стара адреса «вид нарахування борги») */}
        <Route path="payrolls/debts" element={<EmployeeDebtsPage />} />
        <Route path="payrolls/:id" element={<PayrollEditor />} />
        <Route path="payroll-payments" element={<PayrollPaymentsPage />} />
        <Route path="payroll-payments/:id" element={<PayrollPaymentEditor />} />
        <Route path="payroll-payments/:id/print" element={<PayrollPaymentPrintPage />} />
        <Route path="tax-remittances" element={<TaxRemittancesPage />} />
        <Route path="fund-receipts" element={<FundReceiptsPage />} />
        <Route path="fund-receipts/:id" element={<FundReceiptEditor />} />
        <Route path="tax-remittances/:id" element={<TaxRemittanceEditor />} />
        <Route path="salary-deposits" element={<SalaryDepositsPage />} />
        <Route path="salary-deposits/:id" element={<SalaryDepositEditor />} />
        <Route path="payrolls/:id/print" element={<PayrollPrintPage />} />
        <Route path="timesheets" element={<TimesheetsPage />} />
        <Route path="timesheets/:id" element={<TimesheetEditor />} />
        <Route path="daily-timesheets/:id" element={<DailyTimesheetEditor />} />
        {/* Підпорція 1.5: legacy /fixed-assets, /fa-* routes видалено. */}
        <Route path="employees" element={<EmployeesPage />} />
        <Route path="employees/new-unified" element={<EmployeeWithDocumentPage />} />
        <Route path="employees/:id" element={<EmployeeEditor />} />
        <Route path="employees/:id/print" element={<EmployeeCertificatePrintPage />} />
        <Route path="individuals" element={<IndividualsPage />} />
        {/* ч.1059: картка фізособи — окрема сторінка зі своєю адресою. */}
        <Route path="individuals/new" element={<IndividualCardPage />} />
        <Route path="individuals/:id" element={<IndividualCardPage />} />
        <Route path="positions" element={<PositionsPage />} />
        <Route path="profession-classifier" element={<ProfessionClassifierPage />} />
        <Route path="settings/number-schemes" element={<NumberSchemesPage />} />
        <Route path="profile" element={<ProfilePage />} />
        {/* ч.958: «Обліковий запис» — сторінка з розділами (клік по юзеру у хідері). */}
        <Route path="account" element={<AccountPage />} />
        <Route path="account/:section" element={<AccountPage />} />
        <Route path="admin" element={<AdminPage />} />
        {/* /team об'єднано у /admin?tab=users (Порція Виробництво.5 UX). */}
        <Route path="team" element={<Navigate to="/admin?tab=users" replace />} />
        {/* ч.1083: інструкція і файли обробки — «Крок 1» сторінки майстра. */}
        <Route path="migration/from-1c" element={<Navigate to="/migration/wizard" replace />} />
        <Route path="migration/wizard" element={<MigrationWizardPage />} />
        <Route path="migration/runs" element={<MigrationRunsListPage />} />
        <Route path="migration/checkup" element={<MigrationCheckupPage />} />
        <Route path="reports/financial" element={<FinancialStatementsPage />} />
        <Route path="reports/financial/view" element={<FinancialReportsPage />} />
        <Route path="reports/financial/new" element={<FinancialStatementCreatePage />} />
        <Route path="reports/financial/:id" element={<FinancialStatementEditor />} />
        <Route path="reports/financial/:id/print" element={<FinancialStatementPrintPage />} />
        <Route path="reports/tax" element={<TaxReportsPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="reports/project-pnl" element={<ProjectPLReportPage />} />
        <Route path="reports/profitability" element={<ProfitabilityReportPage />} />
        <Route path="reports/cash-flow" element={<CashFlowReportPage />} />
        <Route path="finance" element={<FinancePage />} />
        {/* ч.1376: Журнал доходів і витрат ФОП-єдинника (облік лише за журналом) */}
        <Route path="accounting/income-journal" element={<IncomeJournalPage />} />
        <Route path="accounting/income-journal/print" element={<IncomeJournalPrintPage />} />
        <Route path="departments" element={<DepartmentsPage />} />
        <Route path="hr-orders" element={<HrOrdersPage />} />
        <Route path="hire-orders" element={<HireOrdersPage />} />
        <Route path="hire-orders/:id" element={<HireOrderEditor />} />
        <Route path="hire-orders/:id/print" element={<HireOrderPrintPage />} />
        <Route path="civil-law-contracts" element={<CivilLawContractsPage />} />
        <Route path="civil-law-contracts/:id" element={<CivilLawContractEditor />} />
        <Route path="dps-cabinet" element={<DpsCabinetPage />} />
        <Route path="dps-cabinet/new" element={<DpsCabinetNewPage />} />
        <Route path="dps-cabinet/requests/new" element={<DpsRequestCreatePage />} />
        <Route path="dps-cabinet/requests/:id" element={<DpsRequestCreatePage />} />
        <Route path="tax-reports-j05" element={<TaxReportsJ05Page />} />
        <Route path="tax-reports-j05/new" element={<TaxReportJ05Editor />} />
        <Route path="tax-reports-j05/:id" element={<TaxReportJ05Editor />} />
        <Route path="hire-notifications/:id" element={<HireNotificationEditor />} />
        <Route path="hire-notifications/:id/print" element={<HireNotificationPrintPage />} />
        <Route path="staff-transfers" element={<StaffTransfersPage />} />
        <Route path="staff-transfers/:id" element={<StaffTransferEditor />} />
        <Route path="staff-transfers/:id/print" element={<StaffTransferPrintPage />} />
        <Route path="dismissal-orders" element={<DismissalOrdersPage />} />
        <Route path="dismissal-orders/:id" element={<DismissalOrderEditor />} />
        {/* ч.1209: наказ про тривалу відсутність (догляд за дитиною / служба / призупинення). */}
        <Route path="absence-orders/new" element={<AbsenceOrderEditor />} />
        <Route path="absence-orders/:id" element={<AbsenceOrderEditor />} />
        <Route path="dismissal-orders/:id/print" element={<DismissalOrderPrintPage />} />
        <Route path="reports/vat-summary" element={<VatSummaryPage />} />
        <Route path="reports/vat-declaration-print" element={<VatDeclarationPrintPage />} />
        <Route path="bank-import" element={<BankImportPage />} />
        <Route path="bank-api" element={<BankApiConnectionsPage />} />
        {/* ч.1120: стару сторінку закриття прибрано — закриття проводять і
            розпроводять документом «Закриття місяця»; заборона змін — там само. */}
        <Route path="closing/month" element={<Navigate to="/period-close/runs" replace />} />
        {/* Роздрібний блок list-pages (Порція Роздріб.5a). Editor-pages — Порція 5b. */}
        <Route path="retail/price-groups" element={<PriceGroupsPage />} />
        <Route path="retail/price-groups/:id" element={<PriceGroupEditor />} />
        <Route path="retail/nomenclature-retail-prices" element={<NomenclatureRetailPricesPage />} />
        <Route path="retail/nomenclature-retail-prices/:id" element={<NomenclatureRetailPriceEditor />} />
        <Route path="retail/pos-workstations" element={<PosWorkstationsPage />} />
        <Route path="retail/pos-workstations/:id" element={<PosWorkstationEditor />} />
        <Route path="retail/trade-equipment" element={<TradeEquipmentPage />} />
        <Route path="retail/trade-equipment/:id" element={<TradeEquipmentEditor />} />
        <Route path="warehouse/orders" element={<WarehouseOrdersPage />} />
        <Route path="warehouse/orders/:id" element={<WarehouseOrderEditor />} />
        <Route path="refs/tax-purpose-rules" element={<TaxPurposeRulesPage />} />
        <Route path="taxation/expected-confirmed-vat-adj" element={<EVCVAPage />} />
        <Route path="taxation/expected-confirmed-vat-adj/:id" element={<EVCVAEditor />} />
        <Route path="taxation/vat-credit-reconciliation" element={<VatCreditReconciliationPage />} />
        <Route path="taxation/advance-payment-registrations" element={<AdvancePaymentsPage />} />
        <Route path="taxation/advance-payment-registrations/new" element={<AdvancePaymentEditor />} />
        <Route path="taxation/advance-payment-registrations/:id" element={<AdvancePaymentEditor />} />
        <Route path="taxation/tax-invoice-appendix-1" element={<Appendix1Page />} />
        <Route path="taxation/proportional-vat-recalculation" element={<PropRecalcPage />} />
        <Route path="taxation/proportional-vat-recalculation/:id" element={<PropRecalcEditor />} />
        <Route path="taxation/proportional-vat-coefficient" element={<PropCoefPage />} />
        <Route path="taxation/tax-purpose-adj-inventory" element={<TPInventoryPage />} />
        <Route path="taxation/tax-purpose-adj-inventory/:id" element={<TPInventoryEditor />} />
        <Route path="taxation/tax-purpose-adj-tzr" element={<TPTzrPage />} />
        <Route path="taxation/tax-purpose-adj-tzr/:id" element={<TPTzrEditor />} />
        <Route path="taxation/proportional-vat-coefficient/:id" element={<PropCoefEditor />} />
        <Route path="taxation/tax-declaration-articles" element={<TaxDeclarationArticlesPage />} />
        <Route path="taxation/customs-numbers-register" element={<CustomsNumbersRegisterPage />} />
        {/* Необоротні активи v2 (2026-08-27): старі шляхи → перелік з фільтром виду;
            групи/методи — на платформі (/platform/system-data?tab=asset-groups). */}
        <Route path="assets/registry" element={<AssetsRegistryPage />} />
        <Route path="assets/card/:kind/:id" element={<AssetCardPage />} />
        <Route path="assets/info" element={<AssetsInfoPage />} />
        <Route path="assets/depreciation-journal" element={<DepreciationJournalPage />} />
        <Route path="assets/low-value-balances" element={<LowValueBalancesPage />} />
        <Route path="assets/account-settings" element={<AssetAccountSettingsPage />} />
        <Route path="assets/fixed" element={<Navigate to="/assets/registry?kind=FIXED_ASSET" replace />} />
        <Route path="assets/intangible" element={<Navigate to="/assets/registry?kind=INTANGIBLE" replace />} />
        <Route path="assets/low-value" element={<Navigate to="/assets/registry?kind=LOW_VALUE" replace />} />
        <Route path="assets/groups" element={<Navigate to="/assets/registry" replace />} />
        <Route path="assets/depreciation-methods" element={<Navigate to="/assets/registry" replace />} />
        <Route path="assets/commissioning" element={<AssetCommissioningPage />} />
        <Route path="assets/commissioning/:id" element={<AssetCommissioningEditor />} />
        <Route path="assets/commissioning/:id/print" element={<AssetCommissioningPrintPage />} />
        <Route path="assets/write-off" element={<AssetWriteOffPage />} />
        <Route path="assets/write-off/:id" element={<AssetWriteOffEditor />} />
        <Route path="assets/write-off/:id/print" element={<AssetWriteOffPrintPage />} />
        {/* 2026-08-31: журнал документів обʼєднано з depreciation-journal (режим «за документами»). */}
        <Route path="assets/depreciation" element={<Navigate to="/assets/depreciation-journal" replace />} />
        <Route path="assets/modernization" element={<Navigate to="/assets/state-change" replace />} />
        <Route path="assets/state-change" element={<StateChangePage />} />
        <Route path="assets/state-change/new" element={<StateChangeEditor />} />
        <Route path="assets/state-change/m/:id" element={<StateChangeEditor docType="m" />} />
        <Route path="assets/state-change/m/:id/print" element={<ModernizationPrintPage />} />
        <Route path="assets/state-change/s/:id" element={<StateChangeEditor docType="s" />} />
        <Route path="assets/state-change/s/:id/print" element={<StateChangePrintPage />} />
        <Route path="assets/transfer" element={<InternalTransferPage />} />
        <Route path="assets/transfer/:id" element={<InternalTransferEditor />} />
        <Route path="assets/transfer/:id/print" element={<InternalTransferPrintPage />} />
        <Route path="assets/tax-purpose" element={<AssetTaxPurposeListPage />} />
        <Route path="assets/tax-purpose/:id" element={<AssetTaxPurposeEditor />} />
        <Route path="assets/inventory" element={<InventoryPage />} />
        <Route path="assets/inventory/:id" element={<InventoryEditor />} />
        <Route path="assets/inventory/:id/print" element={<FixedAssetInventoryPrintPage />} />
        {/* 0445 (2026-08-29): продаж ОЗ = реалізація з рядком-ОЗ; окремий документ прибрано. */}
        <Route path="assets/sale" element={<Navigate to="/goods-sales?doc_kind=FIXED_ASSET" replace />} />
        <Route path="assets/sale/new" element={<Navigate to="/goods-sales/new" replace />} />
        <Route path="assets/sale/:id" element={<Navigate to="/goods-sales?doc_kind=FIXED_ASSET" replace />} />
        <Route path="commission/contracts" element={<CommissionContractsPage />} />
        <Route path="commission/transfer" element={<CommissionTransferPage />} />
        <Route path="commission/sale" element={<CommissionSalePage />} />
        <Route path="commission/settlement" element={<CommissionSettlementPage />} />
        <Route path="refs/production-calendar" element={<ProductionCalendarPage />} />
        <Route path="hr/vacation-orders" element={<VacationOrdersPage />} />
        <Route path="hr/vacation-orders/new" element={<VacationOrderEditor />} />
        <Route path="hr/vacation-orders/:id" element={<VacationOrderEditor />} />
        <Route path="hr/vacation-orders/:id/print" element={<VacationOrderPrintPage />} />
        <Route path="hr/vacation-compensations" element={<VacationCompensationsPage />} />
        <Route path="hr/vacation-compensations/new" element={<VacationCompensationCreatePage />} />
        <Route path="hr/vacation-compensations/:id" element={<VacationCompensationEditor />} />
        <Route path="hr/vacation-calculations" element={<VacationCalculationsPage />} />
        <Route path="hr/vacation-calculations/new" element={<VacationCalculationCreatePage />} />
        <Route path="hr/vacation-calculations/:id" element={<VacationCalculationEditor />} />
        <Route path="hr/sick-leaves" element={<SickLeavesPage />} />
        <Route path="hr/sick-leaves/:id" element={<SickLeaveEditor />} />
        <Route path="hr/sick-leave-calculations" element={<SickLeaveCalculationsPage />} />
        <Route path="period-close/runs" element={<MonthCloseRunsPage />} />
        {/* ч.1031: документ закриття — раніше його не існувало, лише список. */}
        <Route path="period-close/runs/:id" element={<MonthCloseRunPage />} />
        <Route path="refs/accounting-constants" element={<AccountingConstantsPage />} />
        <Route path="fop/single-tax" element={<SingleTaxCalculationsPage />} />
        <Route path="fop/self-esv" element={<FopSelfEsvPaymentsPage />} />
        <Route path="fop/self-wz" element={<FopSelfWzPaymentsPage />} />
        <Route path="fop/reports" element={<FopReportsPage />} />
        <Route path="fop-zso/quarterly" element={<FopZsoQuarterlyCalculationsPage />} />
        <Route path="banking/currency-operations" element={<CurrencyOperationsPage />} />
        <Route path="banking/currency-operations/:id" element={<CurrencyOperationEditor />} />
        <Route path="settlements/reconciliation-acts" element={<ReconciliationActsPage />} />
        <Route path="settlements/reconciliation-acts/:id" element={<ReconciliationActEditor />} />
        <Route path="settlements/powers-of-attorney" element={<PowersOfAttorneyPage />} />
        <Route path="settlements/powers-of-attorney/:id" element={<PowerOfAttorneyEditor />} />
        <Route path="settlements/settlement-inventories" element={<SettlementInventoriesPage />} />
        <Route path="settlements/settlement-inventories/:id" element={<SettlementInventoryEditor />} />
        <Route path="retail/pos-providers" element={<PosProvidersPage />} />
        <Route path="retail/pos-providers/:id" element={<PosProviderEditor />} />
        <Route path="retail/customer-cards" element={<CustomerCardsPage />} />
        <Route path="retail/customer-cards/:id" element={<CustomerCardEditor />} />
        <Route path="retail/loyalty-settings" element={<LoyaltySettingsPage />} />
        <Route path="retail/loyalty-settings/:id" element={<LoyaltySettingsEditor />} />
        <Route path="retail/sales-channels" element={<SalesChannelsPage />} />
        <Route path="retail/sales-channels/:id" element={<SalesChannelEditor />} />
        <Route path="retail/customer-card-issue" element={<CustomerCardIssuesPage />} />
        <Route path="retail/customer-card-issue/:id" element={<CustomerCardIssueEditor />} />
        <Route path="retail/cash-shift-open" element={<CashShiftOpensPage />} />
        <Route path="retail/cash-shift-open/:id" element={<CashShiftOpenEditor />} />
        <Route path="retail/retail-sales" element={<RetailSalesPage />} />
        <Route path="retail/retail-sales/:id" element={<RetailSaleEditor />} />
        <Route path="retail/loyalty-transactions" element={<LoyaltyTransactionsPage />} />
        <Route path="retail/loyalty-transactions/:id" element={<LoyaltyTransactionEditor />} />
        <Route path="retail/retail-returns" element={<RetailReturnsPage />} />
        <Route path="retail/retail-returns/:id" element={<RetailReturnEditor />} />
        <Route path="retail/cash-shift-close" element={<CashShiftClosesPage />} />
        <Route path="retail/cash-shift-close/:id" element={<CashShiftCloseEditor />} />
        <Route path="retail/price-changes" element={<PriceChangesPage />} />
        <Route path="retail/price-changes/:id" element={<PriceChangeEditor />} />
        <Route path="retail/online-store-orders" element={<OnlineStoreOrdersPage />} />
        <Route path="retail/online-store-orders/:id" element={<OnlineStoreOrderEditor />} />
        <Route path="retail/setup-wizard" element={<POSSetupWizard />} />
      </Route>
      {/* POS UI (Порція Роздріб.6) — kiosk-mode БЕЗ AppLayout/sidebar.
          Окремий шлях з власним POSGuard (перевіряє retail.sale.create). */}
      <Route
        path="/pos"
        element={
          <RequireAuth>
            <RequireTenant>
              <POSGuard>
                <POSScreen />
              </POSGuard>
            </RequireTenant>
          </RequireAuth>
        }
      />
      <Route
        path="/pos/receipt/:id/print"
        element={
          <RequireAuth>
            <RequireTenant>
              <POSGuard>
                <POSReceiptPrint />
              </POSGuard>
            </RequireTenant>
          </RequireAuth>
        }
      />
      {/* Platform Admin Блок A (super-admin) — окрема гілка БЕЗ AppLayout/
          RequireTenant. Гейт по платформенному статусу — у PlatformLayout
          (GET /auth/platform-status; не власник → redirect на /). */}
      <Route
        path="/platform"
        element={
          <RequireAuth>
            <PlatformLayout />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/platform/system-data" replace />} />
        <Route path="system-data" element={<PlatformSystemDataPage />} />
        {/* ч.1015 (Етап 5): консоль підтримки — окремий маршрут,
            бо це інший рід роботи, ніж вкладки системних даних. */}
        <Route path="support" element={<SupportConsolePage />} />
        <Route path="support-analytics" element={<SupportAnalyticsPage />} />
        {/* ч.1334: статті довідки — перегляд, правка, доручення агенту-редактору. */}
        <Route path="support-help" element={<SupportHelpPage />} />
        <Route path="support-help/:id" element={<SupportHelpArticlePage />} />
        {/* ч.1125: хто має доступ до панелі — видача й відкликання (лише власник). */}
        <Route path="access" element={<PlatformAccessPage />} />
        {/* ч.1126: посилання для тестувальників і тестові акаунти (лише власник). */}
        <Route path="testers" element={<PlatformTestersPage />} />
        {/* ч.1319: відео-інструкції до сторінок — власник і «редактор відео». */}
        <Route path="video" element={<PlatformVideoPage />} />
        <Route path="video/:id" element={<PlatformVideoGuidePage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
